import { useEffect, useState } from 'react'
import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { CUSTOMER_GENDER_OPTIONS, quickRegisterCustomer } from '@/lib/api/customers'
import {
  formatE164Display,
  formatLocalPhoneInput,
  GCC_COUNTRY_CODES,
  isGccPhoneComplete,
  PHONE_LOCAL_PLACEHOLDER,
  toE164Phone,
} from '@/lib/phone'
import { cn } from '@/lib/utils'

const POS_GENDER_OPTIONS = CUSTOMER_GENDER_OPTIONS.filter(
  (option) => option.value === 'ladies' || option.value === 'gents',
)

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(customer: import('@/types/api').Customer) => void} props.onRegistered
 * @param {string} props.phoneCountry
 * @param {string} [props.initialPhone]
 * @param {boolean} [props.disabled]
 */
export function PosNewGuestModal({
  open,
  onClose,
  onRegistered,
  phoneCountry,
  initialPhone = '',
  disabled = false,
}) {
  const [phone, setPhone] = useState(initialPhone)
  const [name, setName] = useState('')
  const [gender, setGender] = useState('ladies')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setPhone(initialPhone)
    setName('')
    setGender('ladies')
    setError('')
  }, [initialPhone, open])

  const phoneE164 = toE164Phone(phone, phoneCountry)
  const phoneComplete = isGccPhoneComplete(phone, phoneCountry)
  const canSubmit = name.trim().length > 0 && phoneComplete && !submitting && !disabled

  if (!open) return null

  const handleSubmit = async () => {
    if (!canSubmit) return

    setSubmitting(true)
    setError('')
    try {
      const customer = await quickRegisterCustomer({
        name: name.trim(),
        phone: phoneE164,
        gender,
      })
      onRegistered(customer)
      onClose()
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to register guest.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="pos-v2-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="pos-v2-modal pos-v2-modal--narrow"
        role="dialog"
        aria-label="New guest"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#E5E7EB] px-4 py-3">
          <h2 className="text-sm font-bold text-slate-900">New guest</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="space-y-3 p-4">
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Mobile
            </span>
            <div className="flex gap-1.5">
              <select
                value={phoneCountry}
                disabled
                aria-label="Country code"
                className="w-[4.5rem] shrink-0 rounded-md border border-[#E5E7EB] bg-slate-50 px-1.5 py-2 text-[10px] font-semibold text-slate-600"
              >
                {GCC_COUNTRY_CODES.filter((item) => item.code === phoneCountry).map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.label}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="off"
                disabled={disabled || submitting}
                value={phone}
                onChange={(event) => setPhone(formatLocalPhoneInput(event.target.value, phoneCountry))}
                placeholder={PHONE_LOCAL_PLACEHOLDER}
                className="pos-v2-drawer-input min-h-[44px] flex-1"
              />
            </div>
            {phoneComplete ? (
              <p className="mt-1 text-[10px] text-slate-500">{formatE164Display(phoneE164)}</p>
            ) : null}
          </label>

          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Name
            </span>
            <input
              type="text"
              disabled={disabled || submitting}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Guest name"
              className="pos-v2-drawer-input min-h-[44px] w-full"
            />
          </label>

          <div>
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Gender
            </span>
            <div className="flex gap-1.5">
              {POS_GENDER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  disabled={disabled || submitting}
                  onClick={() => setGender(option.value)}
                  className={cn(
                    'min-h-[44px] flex-1 rounded-md text-xs font-semibold capitalize transition-colors',
                    gender === option.value
                      ? 'bg-primary text-primary-foreground'
                      : 'border border-[#E5E7EB] bg-white text-slate-600 hover:border-primary/40',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {error ? (
            <p className="text-xs text-destructive">{error}</p>
          ) : null}
        </div>

        <div className="flex gap-2 border-t border-[#E5E7EB] p-4">
          <Button
            type="button"
            variant="outline"
            className="min-h-[44px]"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="min-h-[44px] flex-1"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {submitting ? 'Saving…' : 'Add to ticket'}
          </Button>
        </div>
      </div>
    </div>
  )
}
